/**
 * Subtle scroll parallax for background decorative layers (not `top`).
 *
 * @package TotalDigital
 */

// Hydrate lazy background layers after page load (non-critical layers
// are deferred to avoid competing with the LCP resource on mobile).
const hydrateLazyBgs = () => {
	document.querySelectorAll('.background__layer[data-bg-lazy]').forEach((el) => {
		const url = el.dataset.bg;
		if (url) {
			el.style.setProperty('--bg', `url('${url}')`);
		}
		el.removeAttribute('data-bg-lazy');
	});
};

if (document.readyState === 'complete') {
	hydrateLazyBgs();
} else {
	window.addEventListener('load', hydrateLazyBgs);
}

/**
 * Per-layer scroll oscillator: offset = sin(scrollY * freq + phase) * amp.
 * Different freq / phase / amp = visibly different motion; sine loops forever (no clamp plateau).
 *
 * @typedef {{ freq: number; phase: number; amp: number }} LayerOscillation
 * freq — radians per px scroll (higher = more cycles over the same page height)
 * phase — radians, desyncs layers
 * amp — peak |translateY| in px (max ~100)
 */
/** @type {Record<string, LayerOscillation>} */
const LAYER_OSCILLATION = {
	'fixed-dots': { freq: 0.00235, phase: 0.7, amp: 88 },
	'dot-left': { freq: -0.0031, phase: 2.15, amp: 100 },
	'dot-center': { freq: 0.00405, phase: 4.02, amp: 76 },
	'dot-right': { freq: -0.00265, phase: 1.33, amp: 92 },
	'splash-left': { freq: 0.00345, phase: 5.1, amp: 84 },
	'splash-right': { freq: -0.00385, phase: 0.25, amp: 96 },
	'accent-dots': { freq: 0.00295, phase: 3.67, amp: 72 },
	shapes: { freq: -0.00435, phase: 1.9, amp: 100 },
	leaves: { freq: 0.00255, phase: 4.88, amp: 80 },
};

/** Fallback when data-parallax-layer is unknown */
const DEFAULT_OSCILLATION = { freq: 0.003, phase: 0, amp: 85 };

let rafId = null;
let scrollHandler = null;

const shouldSkip = () => {
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		return true;
	}

	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		return true;
	}

	const { pathname } = window.location;

	if (pathname.includes('/wp-admin/') || pathname.includes('/wp-login.php')) {
		return true;
	}

	if (document.body?.classList.contains('wp-admin')) {
		return true;
	}

	if (window.self !== window.top) {
		return true;
	}

	return !document.querySelector('.background [data-parallax-layer]');
};

const getScrollY = () => window.scrollY || window.pageYOffset || 0;

const updateParallaxLayers = () => {
	const y = getScrollY();

	document.querySelectorAll('.background [data-parallax-layer]').forEach((el) => {
		const key = el.dataset.parallaxLayer || '';
		const { freq, phase, amp } = LAYER_OSCILLATION[key] ?? DEFAULT_OSCILLATION;
		const offset = Math.sin(y * freq + phase) * amp;

		el.style.transform = `translate3d(0, ${offset}px, 0)`;
	});

	rafId = null;
};

const scheduleUpdate = () => {
	if (rafId !== null) {
		return;
	}

	rafId = window.requestAnimationFrame(updateParallaxLayers);
};

const clearLayerTransforms = () => {
	document.querySelectorAll('.background [data-parallax-layer]').forEach((el) => {
		el.style.transform = '';
	});
};

const destroyBackgroundParallax = () => {
	if (rafId !== null) {
		window.cancelAnimationFrame(rafId);
		rafId = null;
	}

	if (scrollHandler) {
		window.removeEventListener('scroll', scrollHandler);
		window.removeEventListener('resize', scrollHandler);
		scrollHandler = null;
	}

	clearLayerTransforms();
};

const initBackgroundParallax = () => {
	destroyBackgroundParallax();

	if (shouldSkip()) {
		return;
	}

	scrollHandler = () => {
		scheduleUpdate();
	};

	window.addEventListener('scroll', scrollHandler, { passive: true });
	window.addEventListener('resize', scrollHandler, { passive: true });
	scheduleUpdate();
};

document.addEventListener('DOMContentLoaded', initBackgroundParallax);
document.addEventListener('swup:content:replace', initBackgroundParallax);

// ---------------------------------------------------------------------------
// [data-flavor] hover/focus → swap decorative vector URLs (base = current page product)
// ---------------------------------------------------------------------------

/** @type {AbortController | null} */
let heroBgSwapAbort = null;

const HERO_BG_FLAVORS = ['original', 'rose', 'sunburst', 'apple'];

const shouldSkipHeroBgSwap = () => {
	if (typeof document === 'undefined') return true;
	if (document.body?.classList.contains('wp-admin')) return true;
	if (window.self !== window.top) return true;
	return !document.getElementById('wcc-hero-bg-urls') || !document.querySelector('.background[data-hero-bg-swap]');
};

const destroyHeroBgSwap = () => {
	if (heroBgSwapAbort) {
		heroBgSwapAbort.abort();
		heroBgSwapAbort = null;
	}
};

/**
 * @param {HTMLElement} background
 * @param {string} flavor
 * @param {Record<string, Record<string, string>>} bgUrls
 */
const applyHeroBgFlavor = (background, flavor, bgUrls) => {
	background.querySelectorAll('.background__layer[data-layer]').forEach((layer) => {
		const key = layer.dataset.layer || '';
		const row = key ? bgUrls[key] : undefined;
		// Base flavour is not duplicated in JSON as a flavour key (see background/index.php).
		const url = row?.[flavor] ?? row?.base;
		if (!url) return;
		layer.style.setProperty('--bg', `url('${url}')`);
		layer.dataset.bg = url;
	});
};

/**
 * @param {HTMLElement} background
 * @param {Record<string, Record<string, string>>} bgUrls
 */
const restoreHeroBgBase = (background, bgUrls) => {
	background.querySelectorAll('.background__layer[data-layer]').forEach((layer) => {
		const key = layer.dataset.layer || '';
		const row = key ? bgUrls[key] : undefined;
		const url = row?.base;
		if (!url) return;
		layer.style.setProperty('--bg', `url('${url}')`);
		layer.dataset.bg = url;
	});
};

/** Any interactive element with this attribute participates in hero background swap. */
const FLAVOR_SELECTOR = '[data-flavor]';

/**
 * @param {Element} el
 * @returns {string | undefined}
 */
const flavorFromElement = (el) => {
	const raw = el instanceof HTMLElement ? el.dataset.flavor : '';
	if (!raw || typeof raw !== 'string') {
		return undefined;
	}

	return HERO_BG_FLAVORS.includes(raw) ? raw : undefined;
};

const initHeroBgSwap = () => {
	destroyHeroBgSwap();

	if (shouldSkipHeroBgSwap()) {
		return;
	}

	const dataEl = document.getElementById('wcc-hero-bg-urls');
	if (!dataEl) {
		return;
	}

	const background = document.querySelector('.background[data-hero-bg-swap]');
	if (!background) {
		return;
	}

	let bgUrls;
	try {
		bgUrls = JSON.parse(dataEl.textContent || '');
	} catch {
		return;
	}
	if (!bgUrls || typeof bgUrls !== 'object') {
		return;
	}

	const ac = new AbortController();
	heroBgSwapAbort = ac;
	const { signal } = ac;

	document.querySelectorAll(FLAVOR_SELECTOR).forEach((link) => {
		if (!(link instanceof HTMLElement)) {
			return;
		}
		const flavor = flavorFromElement(link);
		if (!flavor) {
			return;
		}

		link.addEventListener(
			'mouseenter',
			() => {
				applyHeroBgFlavor(background, flavor, bgUrls);
			},
			{ signal }
		);

		link.addEventListener(
			'mouseleave',
			(e) => {
				const rel = e.relatedTarget;
				if (rel instanceof Node && rel.closest(FLAVOR_SELECTOR)) {
					return;
				}
				restoreHeroBgBase(background, bgUrls);
			},
			{ signal }
		);
	});

	document.addEventListener(
		'focusin',
		(e) => {
			const t = e.target;
			if (!(t instanceof Element)) {
				return;
			}
			const item = t.closest(FLAVOR_SELECTOR);
			if (!item) {
				return;
			}
			const flavor = flavorFromElement(item);
			if (flavor) {
				applyHeroBgFlavor(background, flavor, bgUrls);
			}
		},
		{ signal, capture: true }
	);

	document.addEventListener(
		'focusout',
		() => {
			window.requestAnimationFrame(() => {
				const active = document.activeElement;
				if (active instanceof Element) {
					const item = active.closest(FLAVOR_SELECTOR);
					if (item) {
						const flavor = flavorFromElement(item);
						if (flavor) {
							applyHeroBgFlavor(background, flavor, bgUrls);
							return;
						}
					}
				}
				restoreHeroBgBase(background, bgUrls);
			});
		},
		{ signal, capture: true }
	);
};

document.addEventListener('DOMContentLoaded', initHeroBgSwap);
document.addEventListener('swup:content:replace', initHeroBgSwap);

// ---------------------------------------------------------------------------
// White overlay layers — one per element with class .has-white
// ---------------------------------------------------------------------------

const HAS_WHITE_SELECTOR = '.has-white';

let whiteLayerDivs = [];
let whiteLayerResizeHandler = null;
let whiteLayerLoadHandler = null;
let whiteLayerResizeTimeout = null;
/** @type {ResizeObserver[]} */
let whiteLayerResizeObservers = [];
const WHITE_LAYER_DEBOUNCE_MS = 250;

const shouldSkipWhiteLayers = () => {
	if (typeof document === 'undefined') return true;
	if (document.body?.classList.contains('wp-admin')) return true;
	if (window.self !== window.top) return true;
	return !document.querySelector('.background');
};

/**
 * Border box of `section` relative to `container` top-left (matches DevTools layout box).
 * Prefer over offsetHeight/offsetTop — they can disagree with the painted rect.
 *
 * @param {Element} section
 * @param {Element} container
 * @returns {{ top: number; height: number }}
 */
const getSectionBoxRelativeTo = (section, container) => {
	const sectionRect = section.getBoundingClientRect();
	const containerRect = container.getBoundingClientRect();
	return {
		top: sectionRect.top - containerRect.top,
		height: sectionRect.height,
	};
};

const scheduleWhiteLayersUpdate = () => {
	if (whiteLayerResizeTimeout !== null) {
		window.clearTimeout(whiteLayerResizeTimeout);
	}
	whiteLayerResizeTimeout = window.setTimeout(() => {
		updateWhiteLayers();
		whiteLayerResizeTimeout = null;
	}, WHITE_LAYER_DEBOUNCE_MS);
};

const getOffsetFromElement = (el) => {
	const val = el.getAttribute('data-white-offset');
	if (val === null || val === '') return 0;
	const n = parseInt(val, 10);
	return Number.isNaN(n) ? 0 : n;
};

const updateWhiteLayers = () => {
	const background = document.querySelector('.background');
	if (!background || shouldSkipWhiteLayers()) return;

	const sections = Array.from(document.querySelectorAll(HAS_WHITE_SELECTOR));

	sections.forEach((section, i) => {
		const box = getSectionBoxRelativeTo(section, background);
		const offset = getOffsetFromElement(section);
		const layerTop = Math.round(box.top + offset);
		const layerHeight = Math.round(box.height);

		let div = whiteLayerDivs[i];
		if (!div) {
			div = document.createElement('div');
			div.className = `background__layer background__layer--white-${i + 1}`;
			background.appendChild(div);
			whiteLayerDivs[i] = div;
		}

		div.style.top = `${layerTop}px`;
		div.style.height = `${layerHeight}px`;
		div.style.width = '100%';
		div.style.left = '0';
	});

	whiteLayerDivs.splice(sections.length).forEach((d) => {
		if (d?.parentNode) d.parentNode.removeChild(d);
	});
	whiteLayerDivs.length = sections.length;
};

const destroyWhiteLayers = () => {
	whiteLayerResizeObservers.forEach((ro) => ro.disconnect());
	whiteLayerResizeObservers = [];

	if (whiteLayerResizeHandler) {
		window.removeEventListener('resize', whiteLayerResizeHandler);
		whiteLayerResizeHandler = null;
	}
	if (whiteLayerLoadHandler) {
		window.removeEventListener('load', whiteLayerLoadHandler);
		whiteLayerLoadHandler = null;
	}
	if (whiteLayerResizeTimeout !== null) {
		window.clearTimeout(whiteLayerResizeTimeout);
		whiteLayerResizeTimeout = null;
	}
	whiteLayerDivs.forEach((div) => {
		if (div?.parentNode) div.parentNode.removeChild(div);
	});
	whiteLayerDivs = [];
};

const initWhiteLayers = () => {
	destroyWhiteLayers();
	if (shouldSkipWhiteLayers()) return;

	whiteLayerResizeHandler = () => {
		scheduleWhiteLayersUpdate();
	};

	window.addEventListener('resize', whiteLayerResizeHandler);

	const background = document.querySelector('.background');
	if (background) {
		const bgRo = new ResizeObserver(() => {
			updateWhiteLayers();
		});
		bgRo.observe(background);
		whiteLayerResizeObservers.push(bgRo);
	}

	document.querySelectorAll(HAS_WHITE_SELECTOR).forEach((section) => {
		const secRo = new ResizeObserver(() => {
			updateWhiteLayers();
		});
		secRo.observe(section);
		whiteLayerResizeObservers.push(secRo);
	});

	updateWhiteLayers();
	requestAnimationFrame(() => {
		updateWhiteLayers();
	});

	if (document.fonts?.ready) {
		document.fonts.ready.then(() => {
			if (shouldSkipWhiteLayers()) return;
			updateWhiteLayers();
		});
	}

	whiteLayerLoadHandler = () => {
		if (shouldSkipWhiteLayers()) return;
		updateWhiteLayers();
	};
	window.addEventListener('load', whiteLayerLoadHandler, { once: true });
};

document.addEventListener('DOMContentLoaded', initWhiteLayers);
document.addEventListener('swup:content:replace', initWhiteLayers);

// ---------------------------------------------------------------------------
// White-top overlay layers — one per element with class .has-white-top
// ---------------------------------------------------------------------------

const HAS_WHITE_TOP_SELECTOR = '.has-white-top';

let whiteTopLayerDivs = [];
let whiteTopLayerResizeHandler = null;
let whiteTopLayerLoadHandler = null;
let whiteTopLayerResizeTimeout = null;
/** @type {ResizeObserver[]} */
let whiteTopLayerResizeObservers = [];

const shouldSkipWhiteTopLayers = () => {
	if (typeof document === 'undefined') return true;
	if (document.body?.classList.contains('wp-admin')) return true;
	if (window.self !== window.top) return true;
	return !document.querySelector('.background');
};

const scheduleWhiteTopLayersUpdate = () => {
	if (whiteTopLayerResizeTimeout !== null) {
		window.clearTimeout(whiteTopLayerResizeTimeout);
	}
	whiteTopLayerResizeTimeout = window.setTimeout(() => {
		updateWhiteTopLayers();
		whiteTopLayerResizeTimeout = null;
	}, WHITE_LAYER_DEBOUNCE_MS);
};

const getOffsetFromWhiteTopElement = (el) => {
	const val = el.getAttribute('data-white-top-offset');
	if (val === null || val === '') return 0;
	const n = parseInt(val, 10);
	return Number.isNaN(n) ? 0 : n;
};

const updateWhiteTopLayers = () => {
	const background = document.querySelector('.background');
	if (!background || shouldSkipWhiteTopLayers()) return;

	const sections = Array.from(document.querySelectorAll(HAS_WHITE_TOP_SELECTOR));

	sections.forEach((section, i) => {
		const box = getSectionBoxRelativeTo(section, background);
		const offset = getOffsetFromWhiteTopElement(section);
		const layerTop = Math.round(box.top + offset);
		const layerHeight = Math.round(box.height);

		let div = whiteTopLayerDivs[i];
		if (!div) {
			div = document.createElement('div');
			div.className = `background__layer background__layer--white-top-${i + 1}`;
			background.appendChild(div);
			whiteTopLayerDivs[i] = div;
		}

		div.style.top = `${layerTop}px`;
		div.style.height = `${layerHeight}px`;
		div.style.width = '100%';
		div.style.left = '0';
	});

	whiteTopLayerDivs.splice(sections.length).forEach((d) => {
		if (d?.parentNode) d.parentNode.removeChild(d);
	});
	whiteTopLayerDivs.length = sections.length;
};

const destroyWhiteTopLayers = () => {
	whiteTopLayerResizeObservers.forEach((ro) => ro.disconnect());
	whiteTopLayerResizeObservers = [];

	if (whiteTopLayerResizeHandler) {
		window.removeEventListener('resize', whiteTopLayerResizeHandler);
		whiteTopLayerResizeHandler = null;
	}
	if (whiteTopLayerLoadHandler) {
		window.removeEventListener('load', whiteTopLayerLoadHandler);
		whiteTopLayerLoadHandler = null;
	}
	if (whiteTopLayerResizeTimeout !== null) {
		window.clearTimeout(whiteTopLayerResizeTimeout);
		whiteTopLayerResizeTimeout = null;
	}
	whiteTopLayerDivs.forEach((div) => {
		if (div?.parentNode) div.parentNode.removeChild(div);
	});
	whiteTopLayerDivs = [];
};

const initWhiteTopLayers = () => {
	destroyWhiteTopLayers();
	if (shouldSkipWhiteTopLayers()) return;

	whiteTopLayerResizeHandler = () => {
		scheduleWhiteTopLayersUpdate();
	};

	window.addEventListener('resize', whiteTopLayerResizeHandler);

	const background = document.querySelector('.background');
	if (background) {
		const bgRo = new ResizeObserver(() => {
			updateWhiteTopLayers();
		});
		bgRo.observe(background);
		whiteTopLayerResizeObservers.push(bgRo);
	}

	document.querySelectorAll(HAS_WHITE_TOP_SELECTOR).forEach((section) => {
		const secRo = new ResizeObserver(() => {
			updateWhiteTopLayers();
		});
		secRo.observe(section);
		whiteTopLayerResizeObservers.push(secRo);
	});

	updateWhiteTopLayers();
	requestAnimationFrame(() => {
		updateWhiteTopLayers();
	});

	if (document.fonts?.ready) {
		document.fonts.ready.then(() => {
			if (shouldSkipWhiteTopLayers()) return;
			updateWhiteTopLayers();
		});
	}

	whiteTopLayerLoadHandler = () => {
		if (shouldSkipWhiteTopLayers()) return;
		updateWhiteTopLayers();
	};
	window.addEventListener('load', whiteTopLayerLoadHandler, { once: true });
};

document.addEventListener('DOMContentLoaded', initWhiteTopLayers);
document.addEventListener('swup:content:replace', initWhiteTopLayers);
